import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["form", "input", "clear", "dialog", "results", "initialFocus", "background"]

  connect() {
    this.connected = true
    this.pendingRequest = null
    this.selectedCard = null
    this.selectedPreview = null
    this.restoreFocusAfterRequest = null
    this.observeHeader()
    this.updateClearControl()
  }

  disconnect() {
    this.connected = false
    this.pendingRequest = null
    this.restoreFocusAfterRequest = null
    if (this.headerObserver) this.headerObserver.disconnect()
    this.element.style.removeProperty("--public-report-header-bottom")
    this.closeDialog({ restoreFocus: false })
  }

  submit(event) {
    event.preventDefault()
    if (this.pendingRequest) return
    const query = this.inputTarget.value.trim()
    this.inputTarget.value = query
    this.updateClearControl()
    if (!query) {
      this.reset()
      return
    }
    // Disabled controls are omitted from FormData, so serialize before loading starts.
    const url = new URL(this.formTarget.action, window.location.origin)
    url.search = new URLSearchParams(new FormData(this.formTarget)).toString()
    this.loadResults(url, { openDialog: true })
  }

  paginate(event) {
    event.preventDefault()
    if (this.pendingRequest) return
    this.loadResults(new URL(event.currentTarget.href, window.location.origin), { focusResults: true })
  }

  async loadResults(url, { openDialog = false, focusResults = false } = {}) {
    if (this.pendingRequest) return
    const request = {}
    this.pendingRequest = request
    this.clearSelection()
    this.renderTemplate("loading")
    this.setLoading(true)
    if (openDialog) {
      this.openDialog()
    } else {
      this.initialFocusTarget.focus()
    }
    try {
      const response = await fetch(url.toString(), {
        headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" }
      })
      const fragment = await response.text()
      if (!this.accepts(request)) return
      this.resultsTarget.innerHTML = fragment
      if (response.ok && focusResults) this.focusResultsStatus()
    } catch (_error) {
      if (this.accepts(request)) this.renderTemplate("error")
    } finally {
      if (this.pendingRequest === request) {
        this.pendingRequest = null
        this.setLoading(false)
        if (this.restoreFocusAfterRequest) {
          this.restoreFocusAfterRequest.focus()
          this.restoreFocusAfterRequest = null
        }
      }
    }
  }

  clear(event) {
    if (event) event.preventDefault()
    this.inputTarget.value = ""
    this.reset()
  }

  close(event) {
    if (event) event.preventDefault()
    this.closeDialog()
  }

  selectResult(event) {
    event.preventDefault()
    const card = event.currentTarget
    const preview = document.getElementById(card.getAttribute("aria-controls"))
    if (!preview || !this.resultsTarget.contains(preview)) return
    this.clearSelection()
    card.setAttribute("aria-current", "true")
    preview.hidden = false
    this.selectedCard = card
    this.selectedPreview = preview
    this.dialogTarget.classList.add("has-selection")
    const previewScroller = preview.querySelector("[data-preview-scroll]") || preview
    previewScroller.scrollTop = 0
    preview.focus()
  }

  showResults(event) {
    if (event) event.preventDefault()
    this.clearSelection({ restoreFocus: true })
  }

  inputChanged() {
    this.updateClearControl()
  }

  keydown(event) {
    if (event.key !== "Escape" || !this.isOpen()) return

    event.preventDefault()
    this.closeDialog()
  }

  openDialog() {
    if (!this.isOpen()) this.restoreFocusTo = this.inputTarget
    this.restoreFocusAfterRequest = null
    if (this.updateHeaderOffset) this.updateHeaderOffset()

    this.dialogTarget.classList.add("is-open")
    this.dialogTarget.setAttribute("aria-hidden", "false")
    document.body.classList.add("public-report-search-open")
    this.setBackgroundInert(true)
    this.initialFocusTarget.focus()
  }

  closeDialog({ restoreFocus = true } = {}) {
    const wasOpen = this.isOpen()
    if (!this.pendingRequest) this.setLoading(false)
    this.clearSelection()
    this.dialogTarget.classList.remove("is-open")
    this.dialogTarget.setAttribute("aria-hidden", "true")
    document.body.classList.remove("public-report-search-open")
    this.setBackgroundInert(false)

    if (wasOpen && restoreFocus && this.restoreFocusTo) {
      if (this.pendingRequest) {
        this.restoreFocusAfterRequest = this.restoreFocusTo
      } else {
        this.restoreFocusTo.focus()
      }
    }
    this.restoreFocusTo = null
  }

  reset() {
    this.resultsTarget.innerHTML = ""
    this.closeDialog()
    this.updateClearControl()
  }

  setLoading(loading) {
    this.dialogTarget.classList.toggle("is-loading", loading)
    this.formTarget.setAttribute("aria-busy", loading ? "true" : "false")
    Array.from(this.formTarget.elements).forEach((control) => { control.disabled = loading })
  }

  clearSelection({ restoreFocus = false } = {}) {
    const selectedCard = this.selectedCard
    if (selectedCard) selectedCard.setAttribute("aria-current", "false")
    if (this.selectedPreview) this.selectedPreview.hidden = true

    this.dialogTarget.classList.remove("has-selection")
    this.selectedCard = null
    this.selectedPreview = null

    if (restoreFocus && selectedCard && document.contains(selectedCard)) selectedCard.focus()
  }

  accepts(request) {
    return this.connected && this.pendingRequest === request && this.isOpen()
  }

  isOpen() {
    return this.dialogTarget.classList.contains("is-open")
  }

  updateClearControl() {
    this.clearTarget.hidden = this.inputTarget.value.length === 0
  }

  renderTemplate(name) {
    const template = this.dialogTarget.querySelector(
      `template[data-public-report-search-${name}-template]`
    )
    if (template) this.resultsTarget.innerHTML = template.innerHTML
  }

  focusResultsStatus() {
    const list = this.resultsTarget.querySelector(".public-report-search-results__list")
    const status = this.resultsTarget.querySelector(".public-report-search-results__status")
    if (list) list.scrollTop = 0
    if (status) status.focus({ preventScroll: true })
  }

  observeHeader() {
    const header = this.element.querySelector(".public-report-header")
    if (!header || typeof ResizeObserver === "undefined") return

    this.updateHeaderOffset = () => {
      this.element.style.setProperty("--public-report-header-bottom", `${header.getBoundingClientRect().bottom}px`)
    }
    this.headerObserver = new ResizeObserver(this.updateHeaderOffset)
    this.headerObserver.observe(header)
    this.updateHeaderOffset()
  }

  setBackgroundInert(inert) {
    this.backgroundTargets.forEach((element) => { element.inert = inert })
  }
}
